import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Sparkle,
  Clock,
  ShieldCheck,
  CaretDown,
} from "@phosphor-icons/react";
import Logo from "../components/Logo";
import LogoFull from "../components/LogoFull";
import {
  StepOneMock,
  StepTwoMock,
  StepThreeMock,
  StepFourMock,
} from "../components/landing/StepMockups";

// FAQ content lives next to the markup so we can edit it in one place.
// Each item: { q: question, a: ReactNode answer }.
const FAQ_ITEMS = [
  {
    id: "was-ist-et",
    q: "Was ist ET — Das Einstufungs-Tool?",
    a: (
      <>
        <p>
          <strong>ET</strong> ist ein intelligent gestütztes Werkzeug zur
          kollektivvertraglichen Einstufung (KV-Einstufung) von Mitarbeitern in
          österreichischen Unternehmen. Sie übergeben dem Tool die Tätigkeits­beschreibung
          und vorhandene Dokumente, und ET ermittelt:
        </p>
        <ul className="mt-3 list-disc pl-5 space-y-1">
          <li>die passende <strong>Beschäftigungsgruppe</strong></li>
          <li>die zugehörige <strong>Lohn-/Gehaltsstufe</strong></li>
          <li>die <strong>anrechenbaren Vordienstzeiten</strong> gemäß KV</li>
        </ul>
        <p className="mt-3">
          Am Ende erhalten Sie eine nachvollziehbare Empfehlung mit Begründung —
          optional als PDF zum Download für Ihre Personalakten.
        </p>
      </>
    ),
  },
  {
    id: "fuer-wen",
    q: "Für wen ist ET gedacht?",
    a: (
      <>
        <p>
          ET wurde für <strong>Steuerberater, Bilanzbuchhalter, Personal­verantwortliche
          und HR-Teams</strong> entwickelt, die regelmäßig Mitarbeiter nach einem
          österreichischen Kollektiv­vertrag einstufen müssen. Vom Ein-Personen-Betrieb
          mit einem neuen Lehrling bis zur Konzern-Personalabteilung mit Hunderten
          von Mitarbeitern.
        </p>
        <p className="mt-3">
          Bei der Registrierung wählen Sie zwischen drei Paketen (MINI / MIDI /
          MAXI) — abgestimmt auf die Größe Ihres Unternehmens bzw. Ihrer Kanzlei.
        </p>
      </>
    ),
  },
  {
    id: "registrieren",
    q: "Wie bekomme ich Zugang? Wie funktioniert die Registrierung?",
    a: (
      <>
        <ol className="list-decimal pl-5 space-y-2">
          <li>
            Klicken Sie auf <em>„Registrieren"</em> oben rechts.
          </li>
          <li>
            Wählen Sie Ihre <strong>Unternehmensgröße</strong> (MINI, MIDI oder MAXI).
            Erst danach werden die weiteren Eingabefelder eingeblendet.
          </li>
          <li>
            Tragen Sie Firmenname, Ansprechperson, Adresse und optional eine
            abweichende Rechnungs-E-Mail-Adresse ein.
          </li>
          <li>
            Bestätigen Sie Datenschutz und AGB und senden Sie die Bestellung ab.
          </li>
          <li>
            Ihr Account wird <strong>manuell durch unser Team freigeschaltet</strong>.
            Sie erhalten dazu eine E-Mail-Bestätigung. Erst ab diesem Moment können
            Sie sich einloggen und mit der Einstufung beginnen.
          </li>
          <li>
            Die Rechnung folgt anschließend per E-Mail.
          </li>
        </ol>
      </>
    ),
  },
  {
    id: "passwort-vergessen",
    q: "Ich habe mein Passwort vergessen — was tun?",
    a: (
      <>
        <ol className="list-decimal pl-5 space-y-2">
          <li>
            Öffnen Sie die <Link to="/login" className="text-brand-600 underline underline-offset-2 hover:text-brand-700">Login-Seite</Link>.
          </li>
          <li>
            Klicken Sie unterhalb des Login-Buttons auf <em>„Passwort vergessen?"</em>.
          </li>
          <li>
            Geben Sie Ihre registrierte E-Mail-Adresse ein. Sie erhalten innerhalb
            weniger Sekunden eine E-Mail mit einem sicheren Reset-Link.
          </li>
          <li>
            Der Link ist aus Sicherheitsgründen <strong>60 Minuten</strong> gültig
            und kann <strong>nur einmal</strong> verwendet werden. Klicken Sie auf
            den Link und vergeben Sie ein neues Passwort.
          </li>
        </ol>
        <p className="mt-3 text-warm-600">
          Hinweis: Aus Datenschutzgründen erhalten Sie aus Sicherheitsgründen
          immer die gleiche Bestätigungsanzeige — auch wenn die E-Mail-Adresse
          nicht registriert ist. So lässt sich nicht erkennen, welche Adressen
          ein Konto haben.
        </p>
      </>
    ),
  },
  {
    id: "ablauf",
    q: "Wie läuft eine Einstufung konkret ab?",
    a: (
      <>
        <p>
          Die Einstufung erfolgt in vier klaren Schritten:
        </p>
        <ol className="mt-3 list-decimal pl-5 space-y-2">
          <li>
            <strong>KV &amp; Stichtag wählen</strong> — Welcher Kollektivvertrag
            soll herangezogen werden, auf welches Datum bezieht sich die Einstufung?
          </li>
          <li>
            <strong>Tätigkeit beschreiben &amp; Dokumente hochladen</strong> — Das
            Tool liest Tätigkeits­beschreibung und Stellen­profil, vergleicht sie mit dem
            KV und schlägt entweder direkt eine Gruppe vor oder stellt gezielte Rückfragen.
          </li>
          <li>
            <strong>Vordienstzeiten erfassen</strong> — Manuelle Eingabe oder
            automatische Extraktion aus Dienst­zeugnissen / Lebensläufen. Das Tool bewertet
            jede Periode auf Anrechenbarkeit (inkl. KV-spezifischer Höchstgrenzen).
          </li>
          <li>
            <strong>Empfehlung erstellen</strong> — Finale, begründete Einstufung
            mit Beschäftigungsgruppe, Lohn-/Gehaltsstufe und anrechenbarer Dienstzeit.
            Download als PDF möglich.
          </li>
        </ol>
      </>
    ),
  },
  {
    id: "dateiformate",
    q: "Welche Dateiformate kann ich hochladen?",
    a: (
      <>
        <p>
          ET akzeptiert die gängigen Office- und Dokumentenformate:
        </p>
        <ul className="mt-3 list-disc pl-5 space-y-1">
          <li><strong>PDF</strong> (Stellen­beschreibungen, Dienst­zeugnisse, Lebensläufe)</li>
          <li><strong>JPG / PNG</strong> (z. B. fotografierte Dokumente)</li>
          <li><strong>DOCX</strong> (Word-Dokumente)</li>
        </ul>
        <p className="mt-3">
          Maximalgröße pro Datei: <strong>15 MB</strong>. Jeder Upload durchläuft
          einen Virus­scan und wird ausschließlich im Arbeits­speicher verarbeitet —
          nichts wird auf dem Server gespeichert.
        </p>
      </>
    ),
  },
  {
    id: "datenschutz",
    q: "Was passiert mit den Mitarbeiterdaten, die ich hochlade?",
    a: (
      <>
        <p>
          <strong>Nichts wird gespeichert.</strong> Das ist eine zentrale
          Architektur-Entscheidung von ET:
        </p>
        <ul className="mt-3 list-disc pl-5 space-y-1">
          <li>
            Dateien und Texteingaben werden <strong>ausschließlich im Arbeits­speicher</strong>{" "}
            verarbeitet.
          </li>
          <li>
            Nach Abschluss einer Einstufung werden alle Mitarbeiter­daten
            vollständig verworfen.
          </li>
          <li>
            Es gibt <strong>keinen serverseitigen Verlauf</strong> Ihrer
            Mitarbeitereingaben — auch der Admin sieht keine Mitarbeiter­namen
            oder Tätigkeits­details.
          </li>
          <li>
            Lediglich technische Nutzungs­statistiken (welcher KV wurde wie oft
            angewendet, welche Gruppe wurde empfohlen) werden anonymisiert für
            die Service-Qualität ausgewertet.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "kvs",
    q: "Welche Kollektivverträge werden unterstützt?",
    a: (
      <>
        <p>
          ET wird laufend um weitere österreichische Kollektivverträge erweitert.
          Welche KVs derzeit hinterlegt sind, sehen Sie nach dem Login direkt im
          Auswahlmenü in Schritt 1 der Einstufung.
        </p>
        <p className="mt-3">
          Sie benötigen einen KV, der noch nicht enthalten ist? Schreiben Sie uns
          — wir nehmen ihn priorisiert auf. Anfragen bitte über{" "}
          <a
            href="https://www.vorlagenportal.at"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-600 underline underline-offset-2 hover:text-brand-700"
          >
            www.vorlagenportal.at
          </a>
          .
        </p>
      </>
    ),
  },
  {
    id: "empfehlungs-genauigkeit",
    q: "Wie genau ist die Empfehlung?",
    a: (
      <>
        <p>
          ET wertet jeden hinterlegten Kollektivvertrag spezifisch aus —
          Regelwerk, Anrechnungs­regeln und Sonderfälle sind für jeden KV
          einzeln modelliert. In Schritt 2 sehen
          Sie eine <strong>Konfidenz­anzeige</strong> (0–100 %):
        </p>
        <ul className="mt-3 list-disc pl-5 space-y-1">
          <li>
            <strong>≥ 90 %</strong> — das Tool ist sich sicher und schlägt direkt
            eine Gruppe vor.
          </li>
          <li>
            <strong>&lt; 90 %</strong> — Sie sehen mehrere plausible Optionen
            sowie gezielte Rückfragen, mit denen das Tool seine Empfehlung schärft.
          </li>
        </ul>
        <p className="mt-3">
          Die finale Empfehlung in Schritt 4 enthält eine ausformulierte Begründung,
          damit Sie die Logik der Auswertung vollständig nachvollziehen können.
        </p>
      </>
    ),
  },
  {
    id: "verbindlichkeit",
    q: "Ist die Empfehlung rechtsverbindlich?",
    a: (
      <>
        <p>
          <strong>Nein.</strong> ET liefert eine fundierte Einstufungs­empfehlung,
          die HR-, Lohnverrechnungs- und Steuerberatungs-Teams als Arbeits­grundlage
          dient.
        </p>
        <p className="mt-3">
          Die <strong>finale arbeitsrechtliche Verantwortung</strong> für die
          Einstufung verbleibt beim Arbeitgeber bzw. seinem Steuerberater. Wir
          empfehlen, jede ET-Empfehlung durch einen Fachexperten gegenzeichnen
          zu lassen — insbesondere in Grenzfällen oder bei komplexen Mischtätigkeiten.
        </p>
      </>
    ),
  },
  {
    id: "kontakt",
    q: "Wer steht hinter ET? Wo finde ich Hilfe?",
    a: (
      <>
        <p>
          ET wird betrieben von <strong>Vorlagenportal</strong> — Anbieter
          praxisnaher Werkzeuge für österreichische Steuerberater, Personalisten
          und Unternehmer. Bei Fragen zu Funktionen, Zugang oder Vertrag erreichen
          Sie uns über:
        </p>
        <ul className="mt-3 list-disc pl-5 space-y-1">
          <li>
            Web:{" "}
            <a
              href="https://www.vorlagenportal.at"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 underline underline-offset-2 hover:text-brand-700"
            >
              www.vorlagenportal.at
            </a>
          </li>
          <li>
            E-Mail bei technischen Fragen: einfach auf die Bestätigungs- oder
            Freischaltungs-E-Mail antworten — Ihre Anfrage landet direkt bei uns.
          </li>
        </ul>
      </>
    ),
  },
];

function FaqItem({ item }) {
  return (
    <details
      id={item.id}
      data-testid={`faq-item-${item.id}`}
      className="group border-b border-ink-200 last:border-b-0 scroll-mt-24"
    >
      <summary
        className="flex cursor-pointer items-start gap-4 list-none px-1 py-5 select-none focus:outline-none focus-visible:bg-warm-50 focus-visible:rounded-sm"
      >
        <span className="flex-1 font-cabinet text-base sm:text-lg text-ink-900 font-medium tracking-tight">
          {item.q}
        </span>
        <CaretDown
          size={18}
          weight="bold"
          className="mt-1 shrink-0 text-warm-500 transition-transform duration-200 group-open:rotate-180 group-open:text-brand-500"
        />
      </summary>
      <div className="pb-6 px-1 -mt-1 text-sm sm:text-[15px] text-warm-700 leading-relaxed">
        {item.a}
      </div>
    </details>
  );
}

export default function LandingPage() {
  // Smooth-scroll for in-page anchor clicks without changing the URL hash
  // permanently — keeps the page truly one-page.
  const handleAnchor = (id) => (e) => {
    const target = document.getElementById(id);
    if (target) {
      e.preventDefault();
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F5F2EE]" data-testid="landing-page">
      {/* TOP BAR */}
      <header className="sticky top-0 z-30 border-b border-ink-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6 gap-6">
          <a
            href="#home"
            onClick={handleAnchor("home")}
            data-testid="landing-logo"
            className="shrink-0"
          >
            <Logo size="md" />
          </a>

          <div className="flex items-center gap-6 sm:gap-7">
            {/* Minimal anchor nav — visible on tablet+ only */}
            <nav className="hidden md:flex items-center gap-6" aria-label="Hauptnavigation">
              <a
                href="#home"
                onClick={handleAnchor("home")}
                data-testid="landing-nav-home"
                className="text-xs font-medium uppercase tracking-wider text-warm-600 hover:text-ink-900 transition-crisp"
              >
                Home
              </a>
              <a
                href="#funktionsweise"
                onClick={handleAnchor("funktionsweise")}
                data-testid="landing-nav-funktionsweise"
                className="text-xs font-medium uppercase tracking-wider text-warm-600 hover:text-ink-900 transition-crisp"
              >
                Funktionsweise
              </a>
              <a
                href="#faq"
                onClick={handleAnchor("faq")}
                data-testid="landing-nav-faq"
                className="text-xs font-medium uppercase tracking-wider text-warm-600 hover:text-ink-900 transition-crisp"
              >
                FAQ
              </a>
            </nav>

            <div className="hidden md:block h-5 w-px bg-ink-200" aria-hidden="true" />

            <div className="flex items-center gap-3">
              <Link
                to="/login"
                data-testid="landing-header-login"
                className="inline-flex items-center rounded-sm border border-ink-300 bg-white px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wider text-ink-900 transition-crisp hover:border-ink-900 hover:bg-ink-900 hover:text-white"
              >
                Login
              </Link>
              <Link
                to="/register"
                data-testid="landing-header-register"
                className="inline-flex items-center rounded-sm bg-brand-400 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wider text-ink-900 transition-crisp hover:bg-brand-500 hover:text-white"
              >
                Registrieren
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* HERO — split: text left (aligned with header), photo full-bleed right */}
      <section id="home" className="relative flex-1 flex scroll-mt-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[640px] w-full">
          {/* LEFT: text column. paddingLeft aligns with header's max-w-7xl content. */}
          <div
            className="relative z-10 flex flex-col justify-center px-6 sm:px-10 py-20 sm:py-28 lg:pr-12"
            style={{
              paddingLeft:
                "max(1.5rem, calc((100vw - 80rem) / 2 + 1.5rem))",
            }}
          >
            <div className="max-w-xl">
              <div className="inline-flex items-center gap-2 mb-8 px-3 py-1.5 border border-ink-200 bg-white rounded-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-brand-400" />
                <span className="text-[11px] uppercase tracking-widest font-semibold text-warm-700">
                  Neu in Österreich · Made by Vorlagenportal
                </span>
              </div>

              <LogoFull size="xl" className="-ml-1" />

              <p
                className="mt-6 font-cabinet text-xl sm:text-2xl tracking-tight leading-snug text-warm-700"
                data-testid="landing-hero-title"
              >
                Mitarbeiter korrekt einstufen<span className="text-brand-400">.</span>
              </p>

              <p className="mt-8 max-w-xl text-base sm:text-lg text-warm-700 leading-relaxed">
                <span className="font-semibold text-ink-900">ET</span> – das
                Einstufungs-Tool ermittelt die passende Beschäftigungsgruppe,
                Lohn-/Gehaltsstufe und anrechenbare Vordienstzeit nach dem jeweiligen
                Kollektivvertrag — präzise, nachvollziehbar und ohne Speicherung
                von Mitarbeiterdaten.
              </p>

              <div className="mt-10 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                <Link
                  to="/register"
                  data-testid="landing-cta-register"
                  className="inline-flex items-center justify-center gap-2 rounded-sm bg-brand-400 px-7 py-3.5 text-sm font-semibold uppercase tracking-wider text-ink-900 transition-crisp hover:bg-brand-500 hover:text-white"
                >
                  Registrieren
                  <ArrowRight size={14} weight="bold" />
                </Link>
                <Link
                  to="/login"
                  data-testid="landing-cta-login"
                  className="inline-flex items-center justify-center gap-2 rounded-sm border border-ink-300 bg-white px-7 py-3.5 text-sm font-semibold uppercase tracking-wider text-ink-900 transition-crisp hover:border-ink-900 hover:bg-ink-900 hover:text-white"
                >
                  Login
                </Link>
              </div>
            </div>
          </div>

          {/* RIGHT: full-bleed sharp photo with soft left-edge fade into the cream bg */}
          <div className="relative min-h-[280px] lg:min-h-0 order-first lg:order-last">
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: "url('/kv-folder.jpg')" }}
              aria-hidden="true"
            />
            {/* Left-edge gradient fades the photo into the cream bg of the text column.
                Mobile (image on top): fades the bottom edge instead. */}
            <div
              className="absolute inset-0 hidden lg:block pointer-events-none"
              style={{
                background:
                  "linear-gradient(to right, #F5F2EE 0%, rgba(245,242,238,0.4) 14%, rgba(245,242,238,0) 32%)",
              }}
              aria-hidden="true"
            />
            <div
              className="absolute inset-0 lg:hidden pointer-events-none"
              style={{
                background:
                  "linear-gradient(to bottom, rgba(245,242,238,0) 60%, #F5F2EE 100%)",
              }}
              aria-hidden="true"
            />
          </div>
        </div>
      </section>

      {/* TRUST STRIP — three pillars on clean white */}
      <section className="border-t border-ink-200 bg-white">
        <div className="mx-auto max-w-6xl px-6 py-16 grid grid-cols-1 sm:grid-cols-3 gap-10 sm:gap-8">
          {[
            {
              icon: Sparkle,
              title: "Richtige Einstufung",
              text:
                "Passende Beschäftigungsgruppe, Lohn-/Gehaltsstufe und anrechenbare Vordienstzeiten nach dem jeweiligen Kollektivvertrag — nachvollziehbar und präzise.",
            },
            {
              icon: Clock,
              title: "Zeit sparen",
              text:
                "Einstufungen und Vordienstzeiten in wenigen Minuten berechnen.",
            },
            {
              icon: ShieldCheck,
              title: "Datenschutz im Fokus",
              text:
                "Mitarbeiterdaten und Uploads bleiben vertraulich und werden nicht gespeichert.",
            },
          ].map(({ icon: Icon, title, text }) => (
            <div key={title} className="text-left">
              <div
                className="inline-flex items-center justify-center w-11 h-11 rounded-sm bg-brand-50 border border-brand-100 mb-4"
                aria-hidden="true"
              >
                <Icon size={22} weight="duotone" className="text-brand-500" />
              </div>
              <div className="font-cabinet text-base font-semibold text-ink-900 tracking-tight">
                {title}
              </div>
              <p className="mt-1.5 text-sm text-warm-600 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FUNKTIONSWEISE — real product screenshots, 4 steps */}
      <section
        id="funktionsweise"
        data-testid="landing-funktionsweise-section"
        className="border-t border-ink-200 bg-warm-50 scroll-mt-20"
      >
        <div className="mx-auto max-w-6xl px-6 py-20 sm:py-24">
          <div className="mb-14">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-3">
              Funktionsweise
            </div>
            <h2 className="font-cabinet text-3xl sm:text-4xl tracking-tight text-ink-900 font-medium leading-tight">
              So funktioniert ET in 4 Schritten
              <span className="text-brand-400">.</span>
            </h2>
            <p className="mt-4 max-w-2xl text-sm sm:text-base text-warm-700 leading-relaxed">
              Ein Blick ins Werkzeug: Von der KV-Auswahl bis zur fertigen
              Empfehlung — vollständig nachvollziehbar und ohne Speicherung
              von Mitarbeiterdaten.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-x-12 lg:gap-y-16">
            {[
              {
                step: "01",
                title: "Kollektivvertrag & Stichtag wählen",
                body:
                  "Wählen Sie aus den hinterlegten Kollektivverträgen und legen Sie den Stichtag fest — Berufsjahre und laufende Beschäftigungen werden exakt bis zu diesem Datum gerechnet.",
                Mock: StepOneMock,
              },
              {
                step: "02",
                title: "Tätigkeit beschreiben & analysieren",
                body:
                  "Das Tool analysiert Tätigkeitsbeschreibung und Dokumente, zeigt einen Sicherheits-Score und schlägt die passende Beschäftigungsgruppe vor — bei Unsicherheit mit Vergleichs-Optionen und gezielten Rückfragen.",
                Mock: StepTwoMock,
              },
              {
                step: "03",
                title: "Vordienstzeiten erfassen",
                body:
                  "Manuelle Eingabe oder automatische Extraktion aus Versicherungsdatenauszug bzw. Dienstzeugnissen. Jede Periode wird mit Anrechenbarkeit (Ja/Nein) und Begründung gezeigt — vollständig nachvollziehbar.",
                Mock: StepThreeMock,
              },
              {
                step: "04",
                title: "Einstufung & PDF-Export",
                body:
                  "Finale Empfehlung mit Beschäftigungsgruppe, Lohn-/Gehaltsstufe und anrechenbarer Dienstzeit — inkl. ausformulierter Begründung und Handlungsempfehlung. Optional als PDF zum Download für Ihre Personalakten.",
                Mock: StepFourMock,
              },
            ].map(({ step, title, body, Mock }) => (
              <div key={step} className="flex flex-col">
                {/* Editorial mock — pure HTML/SVG, scroll-revealed */}
                <Mock />

                {/* Caption */}
                <div className="pt-5">
                  <div className="flex items-baseline gap-3 mb-2">
                    <span className="text-[11px] uppercase tracking-widest font-semibold text-brand-500">
                      Schritt {step}
                    </span>
                    <span className="h-px flex-1 bg-ink-200" aria-hidden="true" />
                  </div>
                  <h3 className="font-cabinet text-lg sm:text-xl tracking-tight text-ink-900 font-medium leading-snug">
                    {title}
                  </h3>
                  <p className="mt-2 text-sm sm:text-[15px] text-warm-700 leading-relaxed">
                    {body}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Soft CTA tail */}
          <div className="mt-16 flex flex-col sm:flex-row items-start sm:items-center gap-4 border-t border-ink-200 pt-10">
            <p className="text-sm text-warm-700 max-w-xl">
              Bereit, ET selbst zu nutzen? Die Freischaltung erfolgt manuell
              durch unser Team — Sie werden per E-Mail informiert, sobald Ihr
              Konto aktiv ist.
            </p>
            <Link
              to="/register"
              data-testid="funktionsweise-cta-register"
              className="inline-flex items-center justify-center gap-2 rounded-sm bg-brand-400 px-6 py-3 text-xs font-semibold uppercase tracking-wider text-ink-900 transition-crisp hover:bg-brand-500 hover:text-white"
            >
              Registrieren
              <ArrowRight size={13} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section
        id="faq"
        data-testid="landing-faq-section"
        className="border-t border-ink-200 bg-brand-50 scroll-mt-20"
      >
        <div className="mx-auto max-w-6xl px-6 py-20 sm:py-24">
          <div className="mb-12">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-3">
              Häufige Fragen
            </div>
            <h2 className="font-cabinet text-3xl sm:text-4xl tracking-tight text-ink-900 font-medium leading-tight">
              Alles Wichtige auf einen Blick
              <span className="text-brand-400">.</span>
            </h2>
            <p className="mt-4 text-sm sm:text-base text-warm-700 leading-relaxed">
              Antworten zur Funktion, Registrierung, Datenschutz und Kontakt. Sie
              haben darüber hinaus eine Frage? Schreiben Sie uns über{" "}
              <a
                href="https://www.vorlagenportal.at"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-600 underline underline-offset-2 hover:text-brand-700"
              >
                www.vorlagenportal.at
              </a>
              .
            </p>
          </div>

          <div className="border border-ink-200 bg-white rounded-sm px-5 sm:px-8">
            {FAQ_ITEMS.map((item) => (
              <FaqItem key={item.id} item={item} />
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-ink-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <Logo size="sm" />
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-5 text-[11px] text-warm-500">
            <span>
              © {new Date().getFullYear()} ET · Das Einstufungs-Tool by Vorlagenportal
            </span>
            <Link
              to="/impressum"
              className="hover:text-ink-900 transition-crisp underline underline-offset-2 decoration-warm-300 hover:decoration-ink-700"
              data-testid="landing-footer-impressum"
            >
              Impressum
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
